import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, MessageSquare } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ChatDrawer } from "@/components/ChatDrawer";
import { formatUSD, LEAD_STATUS_META } from "@/lib/format";

const MyLeads = () => {
  const [leads, setLeads] = useState(null);
  const [activeThreadId, setActiveThreadId] = useState(null);

  useEffect(() => {
    api.get("/me/leads").then((r) => setLeads(r.data)).catch(() => setLeads([]));
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="my-leads-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Your requests</p>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            My inquiries
          </h1>
        </div>
        <Button asChild variant="outline" className="border-brand text-brand hover:bg-brand hover:text-white">
          <Link to="/account">← Account</Link>
        </Button>
      </div>

      {leads === null ? (
        <div className="mt-10 space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-24 bg-slate-100 rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : leads.length === 0 ? (
        <div className="mt-16 text-center bg-slate-50 border border-slate-200 rounded-2xl py-16" data-testid="leads-empty">
          <MessageSquare size={30} className="mx-auto text-slate-300" />
          <p className="mt-4 font-display text-xl text-slate-800">No inquiries yet</p>
          <p className="mt-2 text-sm text-slate-500">
            Request an appointment on any vehicle and our BDC team will follow up.
          </p>
          <Button asChild className="mt-6 bg-brand hover:bg-brand-hover text-white">
            <Link to="/inventory">Browse inventory</Link>
          </Button>
        </div>
      ) : (
        <div className="mt-10 space-y-4" data-testid="leads-list">
          {leads.map((l) => {
            const meta = LEAD_STATUS_META[l.status] || LEAD_STATUS_META.new;
            return (
              <div
                key={l.id}
                className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col md:flex-row md:items-center gap-5 hover:border-slate-300 transition-all"
                data-testid={`lead-row-${l.id}`}
              >
                <img
                  src={l.vehicle?.images?.[0] || "https://images.unsplash.com/photo-1494976388531-d1058494cdd8?auto=format&fit=crop&w=400&q=80"}
                  alt=""
                  className="w-full md:w-24 h-32 md:h-20 object-cover rounded-lg border border-slate-200"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-display text-lg font-semibold text-slate-900 truncate">
                    {l.vehicle
                      ? `${l.vehicle.year} ${l.vehicle.make} ${l.vehicle.model}`
                      : "Vehicle unavailable"}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {l.dealer?.name || "—"} · Submitted {new Date(l.created_at).toLocaleDateString()}
                  </p>
                  {l.notes && (
                    <p className="mt-2 text-xs text-slate-500 line-clamp-1">"{l.notes}"</p>
                  )}
                </div>
                <div className="flex md:flex-col items-end justify-between md:justify-center gap-2 shrink-0 min-w-[160px]">
                  <span
                    className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold ${meta.tone}`}
                    data-testid={`lead-status-${l.id}`}
                  >
                    {meta.label}
                  </span>
                  {l.vehicle && (
                    <p className="text-xs font-semibold text-slate-700">
                      {formatUSD(l.vehicle.price)}
                    </p>
                  )}
                  {l.chat_thread_id && (
                    <Button
                      size="sm"
                      className="bg-brand hover:bg-brand-hover text-white"
                      onClick={() => setActiveThreadId(l.chat_thread_id)}
                      data-testid={`lead-chat-btn-${l.id}`}
                    >
                      <MessageCircle size={13} /> Chat with BDC
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Shared ChatDrawer — swap threadId via `key` so state resets per thread. */}
      {activeThreadId && (
        <ChatDrawer
          key={activeThreadId}
          threadId={activeThreadId}
          open={!!activeThreadId}
          onOpenChange={(o) => { if (!o) setActiveThreadId(null); }}
        />
      )}
    </div>
  );
};

export default MyLeads;
